import { Fragment } from 'react';

import { makeStyles } from '@material-ui/core';

import isEmpty from 'lodash/isEmpty';

import { CriterionDiff } from './CriterionDiff';
import { Criterion, Expression } from './types';

const useStyles = makeStyles(theme => ({
  group: {
    border: `1px solid ${
      theme.palette.type === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgba(0, 0, 0, 0.1)'
    }`,
    borderRadius: 'var(--bui-radius-2)',
    padding: 'var(--bui-space-2) var(--bui-space-3)',
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--bui-space-1)',
    fontSize: 'var(--bui-font-size-2)',
  },
  operator: {
    fontSize: 'var(--bui-font-size-2)',
    fontWeight: 500,
    color: 'var(--bui-fg-secondary)',
    paddingLeft: 'var(--bui-space-2)',
  },
}));

const OperatorLabel = ({ operator }: { operator: string }) => {
  const classes = useStyles();
  return <div className={classes.operator}>{operator}</div>;
};

const getOperator = (
  oldExpr: Expression | undefined,
  newExpr: Expression | undefined,
): string => {
  if (newExpr?.and) return 'and';
  if (newExpr?.or) return 'or';
  if (oldExpr?.and) return 'and';

  return 'or';
};

export const ExpressionDiff = ({
  oldExpr,
  newExpr,
  oldCriteria,
  newCriteria,
  isNested,
}: {
  oldExpr: Expression | undefined;
  newExpr: Expression | undefined;
  oldCriteria: Record<string, Criterion>;
  newCriteria: Record<string, Criterion>;
  isNested?: boolean;
}) => {
  const classes = useStyles();

  // Leaf refs
  if (oldExpr?.ref || newExpr?.ref) {
    // Different criteria at the same position — show as separate removed/added items
    if (oldExpr?.ref && newExpr?.ref && oldExpr.ref !== newExpr.ref) {
      return (
        <>
          <CriterionDiff
            oldCriterion={oldCriteria[oldExpr.ref]}
            newCriterion={undefined}
          />
          <CriterionDiff
            oldCriterion={undefined}
            newCriterion={newCriteria[newExpr.ref]}
          />
        </>
      );
    }
    return (
      <CriterionDiff
        oldCriterion={oldExpr?.ref ? oldCriteria[oldExpr?.ref] : undefined}
        newCriterion={newExpr?.ref ? newCriteria[newExpr?.ref] : undefined}
      />
    );
  }

  // Not expressions
  if (oldExpr?.not || newExpr?.not) {
    return (
      <ExpressionDiff
        oldExpr={oldExpr?.not}
        newExpr={newExpr?.not}
        oldCriteria={oldCriteria}
        newCriteria={newCriteria}
        isNested={isNested}
      />
    );
  }

  // Groups
  const oldOperands = oldExpr?.and?.operands ?? oldExpr?.or?.operands ?? [];
  const newOperands = newExpr?.and?.operands ?? newExpr?.or?.operands ?? [];
  const operator = getOperator(oldExpr, newExpr);
  const longerList =
    newOperands.length >= oldOperands.length ? newOperands : oldOperands;

  if (isEmpty(longerList)) return null;

  const content = (
    <>
      {longerList.map((_, i) => (
        <Fragment key={i}>
          {i > 0 && <OperatorLabel operator={operator} />}
          <ExpressionDiff
            oldExpr={oldOperands[i]}
            newExpr={newOperands[i]}
            oldCriteria={oldCriteria}
            newCriteria={newCriteria}
            isNested
          />
        </Fragment>
      ))}
    </>
  );

  if (isNested) {
    return <div className={classes.group}>{content}</div>;
  }

  return content;
};
